import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { KeyRound, LogOut, MessagesSquare, RefreshCw, UserRound } from 'lucide-react';
import { reloadApp } from '@/lib/app-update';
import { useAuth } from '@/hooks/use-auth';
import { useHideOnScrollDown } from '@/hooks/useHideOnScrollDown';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsAdmin, usePermission } from '@/hooks/usePermission';
import { hasUnsavedChanges } from '@/utils/unsavedChanges';
import { hasStoredDrafts } from '@/lib/ui-prefs';
import { useCurrentProject } from '@/lib/current-project';
import { confirmDialog } from '@/components/ConfirmDialog';
import { getPageTitle } from '@/navigation/nav-items';
import { useNavItems } from '@/navigation/use-nav-items';
import { usePageTopBarOptional } from '@/components/layout/PageTopBarContext';
import { PageBreadcrumbs } from '@/components/PageBreadcrumbs';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { GlobalSearch } from '@/components/layout/omni-search/GlobalSearch';
import type { OmniEntityKind } from '@/components/layout/omni-search/types';
import { buildOmniNavSections } from '@/components/layout/omni-search/build-nav-sections';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/components/tasks/NotificationBell';
import { LiveUpdatesIndicator } from '@/components/LiveUpdatesIndicator';
import { projectTintStyle } from '@/components/factory/project-tint';
import { MemberProfileDialog } from '@/components/team/MemberProfileDialog';
import { chatPanelHiddenOn, useChatPanel } from '@/pages/chat/chat-panel-state';
import { isMacPlatform } from '@/lib/find-shortcut';

/**
 * Slim sticky bar across the top of every authenticated page: breadcrumb trail on
 * the left, the user menu on the right. Page actions (Edit, Export, etc.)
 * belong in the page body — never here. Global status indicators (live updates,
 * notifications) are an intentional exception. Inside a project it's tinted with the project's
 * colour, so you can tell which project a page is in at a glance.
 */
export function AppTopBar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const topBar = usePageTopBarOptional();
  const breadcrumbs = topBar?.breadcrumbs ?? null;
  const [profileOpen, setProfileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const scrolledAway = useHideOnScrollDown(location.pathname);
  // SF-T-266: a page whose own header stands in for the bar on a phone (a chat).
  const isMobile = useIsMobile();
  const offOnPhone = isMobile && (topBar?.hiddenOnPhone ?? false);
  const topBarHidden = scrolledAway || offOnPhone;
  const project = useCurrentProject();
  const isAdmin = useIsAdmin();
  const navItems = useNavItems();

  useEffect(() => {
    const shell = headerRef.current?.closest<HTMLElement>('[data-slot="app-shell"]');
    shell?.style.setProperty(
      '--app-topbar-height',
      topBarHidden ? '0px' : '3rem'
    );
  }, [topBarHidden]);

  const omniSections = useMemo(() => buildOmniNavSections({ items: navItems, isAdmin }), [navItems, isAdmin]);

  // Tasks are searched in the current project only; saved views there and the person's own on
  // pages outside projects (the master board's), anywhere.
  const canViewTasks = usePermission('PROJECT_VIEW');
  const canIncludeOmniEntity = useCallback(
    (kind: OmniEntityKind) => (kind === 'tasks' ? canViewTasks : true),
    [canViewTasks]
  );

  const fallbackTitle = getPageTitle(location.pathname);
  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';

  // Signing out throws away drafts kept in this browser, so unsaved work asks
  // first: this page's, and drafts other pages left stored.
  const handleLogout = async () => {
    if (hasUnsavedChanges() || hasStoredDrafts()) {
      const ok = await confirmDialog({
        title: 'Log out?',
        description: 'Unsaved changes and drafts kept in this browser will be lost.',
        confirmLabel: 'Discard and log out',
        destructive: true,
      });
      if (!ok) return;
    }
    await logout();
  };

  return (
    <header
      ref={headerRef}
      className={cn(
        'sticky top-0 z-40 flex h-12 shrink-0 items-center gap-2 border-b bg-background px-4 transition-transform duration-200 ease-out',
        topBarHidden && '-translate-y-full pointer-events-none',
        offOnPhone && 'hidden'
      )}
      style={project ? projectTintStyle(project.color) : undefined}
      data-project-colour={project?.color}
    >
      <SidebarTrigger className="-ml-1 md:hidden" />
      <Separator orientation="vertical" className="mr-1 h-4 md:hidden" />
      <div className="min-w-0 flex-1 overflow-hidden">
        {breadcrumbs ?? <PageBreadcrumbs items={[{ label: fallbackTitle }]} />}
      </div>

      {/* Phones: search is just its icon, so the buttons on the right never run into it. */}
      <div className="flex w-auto shrink-0 justify-center sm:w-48">
        <GlobalSearch sections={omniSections} canIncludeEntity={canIncludeOmniEntity} />
      </div>

      {/* Equal flex either side keeps search centred when there's room. These buttons never
          shrink below what they need (so they can't slide under search at in-between widths);
          the breadcrumbs shrink instead. */}
      <div className="flex flex-1 shrink-0 items-center justify-end gap-1 max-sm:flex-none">
        {project ? <LiveUpdatesIndicator /> : null}
        {/* Keyed by project: another project's notifications never show while this one's load. */}
        {project ? <NotificationBell key={project.id} /> : null}
        {isAdmin && !chatPanelHiddenOn(location.pathname) ? <ChatPanelButton /> : null}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="rounded-full" aria-label="Account">
              <MemberAvatar name={userName} photourl={user?.photoURL} size="sm" hint={false} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56">
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <MemberAvatar name={userName} photourl={user?.photoURL} size="sm" hint={false} />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{userName}</span>
                  <span className="truncate text-xs text-muted-foreground">{user?.email || ''}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
              <UserRound />
              Profile
            </DropdownMenuItem>
            {/* A link rather than navigate() in code, so the leave guard sees it. */}
            <DropdownMenuItem asChild>
              <Link to="/settings/api-keys">
                <KeyRound />
                API keys
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => reloadApp()}>
              <RefreshCw />
              Reload app
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void handleLogout()}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {user?.id ? <MemberProfileDialog open={profileOpen} onOpenChange={setProfileOpen} /> : null}
      </div>
    </header>
  );
}

/** Opens and closes the chat panel beside the page (admins, T-78; pages/chat/ChatDock.tsx), also with Ctrl/⌘+. */
function ChatPanelButton() {
  const { open, setOpen } = useChatPanel();
  const shortcut = isMacPlatform() ? '⌘.' : 'Ctrl+.';
  // A keyboard shortcut is an outside system (the window's key presses), so this is an effect.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.repeat || event.key !== '.' || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      event.preventDefault();
      setOpen(!open);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label="Chat beside this page"
      aria-pressed={open}
      aria-keyshortcuts={isMacPlatform() ? 'Meta+.' : 'Control+.'}
      data-hint={`${open ? 'Close the chat panel' : 'Chat beside this page'} (${shortcut})`}
      className={cn(open && 'bg-muted')}
      onClick={() => setOpen(!open)}
    >
      <MessagesSquare className="size-4" />
    </Button>
  );
}

